/**
 * RelatedPanel - メモ・読書ノートの下に出す「関連の提案」。
 * 意味の近い、まだつながっていないカード・メモ・他の本のハイライト・本・AI 解析済みの他の本の本文を出し、
 * その場でリンク・引用できる
 */

import type { Editor } from '@tiptap/react'
import {
  BookOpen,
  BookText,
  ChevronRight,
  ExternalLink,
  FileText,
  Link2,
  Quote,
  Sparkles,
  StickyNote,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { getRelated, type RelatedItem, type RelatedKind } from '@/api/services/related'
import { highlightColorHex, highlightReaderPath } from '@/components/tiptap-node/highlight-ref-node'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { bookNotePath, memoPath, readerPositionPath } from '@/routes/paths'

const GROUPS: { kind: RelatedItem['kind']; label: string; icon: typeof FileText }[] = [
  { kind: 'card', label: 'カード', icon: StickyNote },
  { kind: 'highlight', label: '他の本のハイライト', icon: Quote },
  { kind: 'passage', label: '他の本の本文', icon: BookText },
  { kind: 'book', label: '本', icon: BookOpen },
  { kind: 'memo', label: 'メモ', icon: FileText },
]

type Props = {
  kind: Exclude<RelatedKind, 'highlight'>
  refId: number
  editor: Editor | null
}

const itemPath = (item: RelatedItem): string => {
  if (item.kind === 'highlight') return highlightReaderPath({ id: item.ref_id, episode_id: item.episode_id ?? 0 })
  if (item.kind === 'book') return bookNotePath(item.ref_id)
  if (item.kind === 'passage') return readerPositionPath(item.episode_id ?? 0, item.key ?? 0)
  return memoPath(item.ref_id)
}

export const RelatedPanel = ({ kind, refId, editor }: Props) => {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<RelatedItem[] | null>(null)
  const [pending, setPending] = useState(0)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setItems(null)
    setFailed(false)
    getRelated(kind, refId)
      .then((res) => {
        if (cancelled) return
        setItems(res.items)
        setPending(res.pending)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [open, kind, refId])

  // リンク・引用したものは提案から外す
  const insert = (item: RelatedItem) => {
    if (!editor) return
    const chain = editor.chain().focus()
    if (item.kind === 'highlight') {
      chain
        .insertContent({
          type: 'highlightRef',
          attrs: { highlightId: item.ref_id, text: item.snippet, source: item.title },
        })
        .run()
    } else if (item.kind === 'passage') {
      chain
        .insertContent([
          { type: 'blockquote', content: [{ type: 'paragraph', content: [{ type: 'text', text: item.snippet }] }] },
          { type: 'paragraph', content: [{ type: 'text', text: `— ${item.title}` }] },
        ])
        .run()
    } else {
      chain
        .insertDocLink({
          targetType: item.kind === 'book' ? 'product' : 'memo',
          targetId: item.ref_id,
          label: item.title,
        })
        .run()
    }
    setItems((prev) => prev?.filter((i) => !(i.kind === item.kind && i.ref_id === item.ref_id)) ?? null)
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mx-6 mt-4 border-t pt-3 sm:mx-0">
      <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-muted-foreground">
        <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} />
        <Sparkles className="size-3.5" />
        関連の提案
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        {failed ? (
          <p className="py-4 text-center text-sm text-muted-foreground">読み込めませんでした</p>
        ) : items === null ? (
          <div className="flex justify-center py-4">
            <Spinner size={20} />
          </div>
        ) : (
          <>
            {items.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">意味の近いものは見つかりませんでした</p>
            )}
            {GROUPS.map((group) => {
              const groupItems = items.filter((i) => i.kind === group.kind)
              if (groupItems.length === 0) return null
              return (
                <div key={group.kind} className="mb-3">
                  <p className="mb-1 flex items-center gap-1 px-2 text-xs font-medium text-muted-foreground">
                    <group.icon className="size-3.5" />
                    {group.label}
                  </p>
                  <ul className="space-y-1">
                    {groupItems.map((item) => (
                      <RelatedRow
                        key={`${item.kind}:${item.ref_id}`}
                        item={item}
                        canInsert={!!editor}
                        onInsert={() => insert(item)}
                      />
                    ))}
                  </ul>
                </div>
              )
            })}
            {pending > 0 && (
              <p className="px-2 text-xs text-muted-foreground">
                まだ {pending} 件を解析していません。開き直すと続きを解析して提案が増えます
              </p>
            )}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
}

function RelatedRow({ item, canInsert, onInsert }: { item: RelatedItem; canInsert: boolean; onInsert: () => void }) {
  const isHighlight = item.kind === 'highlight'
  const isQuote = isHighlight || item.kind === 'passage'
  return (
    <li className="flex gap-2 rounded-md px-2 py-1.5 hover:bg-accent/40">
      {isHighlight && (
        <span
          className="w-1 shrink-0 self-stretch rounded-full"
          style={{ backgroundColor: highlightColorHex(item.color ?? 'yellow') }}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {isQuote ? (
          <>
            <p className="line-clamp-3 text-sm">{item.snippet}</p>
            {item.note && <p className="line-clamp-2 text-xs text-muted-foreground">✎ {item.note}</p>}
            <p className="truncate text-[11px] text-muted-foreground">{item.title}</p>
          </>
        ) : (
          <>
            <p className="truncate text-sm">{item.title}</p>
            {item.snippet && <p className="line-clamp-2 text-xs text-muted-foreground">{item.snippet}</p>}
          </>
        )}
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <span title="意味の近さ">{Math.round(item.similarity * 100)}%</span>
          <Link
            to={itemPath(item)}
            className="flex items-center gap-0.5 rounded px-0.5 hover:bg-accent hover:text-foreground"
          >
            <ExternalLink className="size-3" />
            開く
          </Link>
          <button
            type="button"
            onClick={onInsert}
            disabled={!canInsert}
            title={isQuote ? 'カーソル位置に引用' : 'カーソル位置にリンク'}
            className="flex items-center gap-0.5 rounded px-0.5! hover:bg-accent hover:text-foreground disabled:opacity-40"
          >
            {isQuote ? <Quote className="size-3" /> : <Link2 className="size-3" />}
            {isQuote ? '引用' : 'リンク'}
          </button>
        </div>
      </div>
    </li>
  )
}
